<template>
  <section class="invitees-page">
    <AppPageTitleBar
      class="is-hidden-sm-up"
      :title="pageTitle"
      :back-to="backRoute"
      back-label="Natrag"
    >
      <template #actions>
        <ToolbarActionButton
          v-if="canEditInvitees"
          text="Dodaj uzvanika"
          :to="createInviteeRoute"
          :disabled="isEventArchived"
        />
      </template>
    </AppPageTitleBar>

    <div
      v-if="canImportInvitees || canDownloadInviteeQrCodes"
      class="invitees-page__mobile-actions is-hidden-sm-up"
    >
      <v-btn
        v-if="canImportInvitees"
        class="button button--secondary button--toolbar"
        :disabled="isEventArchived || !selectedEvent || appStore.importingInviteesEventId !== null"
        :loading="appStore.importingInviteesEventId === eventId"
        variant="outlined"
        @click="openInviteeImport"
      >
        Uvezi
      </v-btn>
      <v-btn
        v-if="canDownloadInviteeQrCodes"
        class="button button--secondary button--toolbar"
        :loading="inviteeListState.isDownloadingQrCodes"
        variant="outlined"
        @click="downloadInviteeQrCodesAsync"
      >
        Preuzmi QR kodove
      </v-btn>
    </div>

    <input
      ref="inviteeImportInput"
      accept=".xlsx"
      aria-label="Odaberi Excel datoteku za uvoz uzvanika"
      type="file"
      hidden
      @change="importInviteesAsync"
    >

    <section class="page-stack grid-stack grid-gap-page-section">
      <article class="page-panel page-panel--mobile-unframed invitees-panel grid-stack grid-gap-20">
        <div class="grid grid-sm-up grid-row-gap-0 grid-column-gap-16 align-items-center">
          <router-link
            class="back-link col-12 col-sm-4"
            :to="backRoute"
            aria-label="Natrag"
          >
            <v-icon
              icon="mdi-arrow-left"
              size="20"
            />
            <span>Natrag</span>
          </router-link>

          <div class="invitees-panel__actions col-12 col-sm-8 justify-self-end">
            <ToolbarActionButton
              v-if="canEditInvitees"
              text="Dodaj uzvanika"
              :to="createInviteeRoute"
              :disabled="isEventArchived"
            />
            <v-btn
              v-if="canImportInvitees"
              class="button button--secondary button--toolbar"
              :disabled="isEventArchived || !selectedEvent || appStore.importingInviteesEventId !== null"
              :loading="appStore.importingInviteesEventId === eventId"
              variant="outlined"
              @click="openInviteeImport"
            >
              Uvezi
            </v-btn>
            <v-btn
              v-if="canDownloadInviteeQrCodes"
              class="button button--secondary button--toolbar"
              :loading="inviteeListState.isDownloadingQrCodes"
              variant="outlined"
              @click="downloadInviteeQrCodesAsync"
            >
              Preuzmi QR kodove
            </v-btn>
          </div>
        </div>

        <header class="event-context-heading">
          <h2 class="event-context-heading__title">
            Uzvanici
            <span class="event-context-heading__event-name">"{{ selectedEvent?.name }}"</span>
          </h2>
        </header>

        <v-alert
          v-if="inviteeListState.importFeedback"
          :type="inviteeListState.importFeedback.type"
          variant="tonal"
          closable
          @click:close="inviteeListState.importFeedback = null"
        >
          <p>{{ inviteeListState.importFeedback.message }}</p>
          <ul
            v-if="inviteeListState.importFeedback.rows.length"
            class="invitees-panel__import-rows"
          >
            <li
              v-for="(row, index) in inviteeListState.importFeedback.rows"
              :key="`${row.row}-${row.column ?? index}`"
            >
              <template v-if="row.column">
                Redak {{ row.row }}, stupac {{ row.column }} ({{ row.label }}): {{ row.message }}
              </template>
              <template v-else>
                Redak {{ row.row }}: {{ row.email }}
              </template>
            </li>
          </ul>
        </v-alert>

        <Filters
          v-model="inviteeListState.filters"
          :filter-fields="inviteeFilterFields"
          @change="resetInviteeListPage"
        />

        <v-table class="table table--framed invitees-table">
          <tbody>
            <tr
              v-for="invitee in inviteeListState.eventInvitees"
              :key="invitee.id"
              class="invitees-table__row"
              @click="openInviteeDetail(invitee)"
            >
              <td class="table__person-cell invitees-table__name">
                <span class="table__primary-text">{{ invitee.name }}</span>
                <span class="table__secondary-text is-hidden-sm-up">{{ invitee.email }}</span>
              </td>
              <td class="is-hidden-sm-down">{{ invitee.company }}</td>
              <td class="is-hidden-sm-down">{{ invitee.email }}</td>
              <td @click.stop>
                <v-menu
                  content-class="table-menu"
                  location="bottom end"
                >
                  <template #activator="{ props: menuProps }">
                    <v-btn
                      v-bind="menuProps"
                      aria-label="Otvori akcije za uzvanika"
                      class="button table__actions-button"
                      icon="mdi-dots-horizontal"
                      size="small"
                      variant="text"
                    />
                  </template>

                  <div class="table-menu__content grid-stack grid-gap-16">
                    <v-btn
                      class="button button--menu"
                      :to="{
                        name: 'event-invitee-detail',
                        params: {
                          eventId,
                          inviteeId: invitee.id,
                        },
                      }"
                      variant="text"
                    >
                      Detalji uzvanika
                    </v-btn>
                    <v-btn
                      v-if="canEditInvitees"
                      class="button button--menu"
                      :disabled="isEventArchived"
                      :to="{
                        name: 'event-invitee-edit',
                        params: {
                          eventId,
                          inviteeId: invitee.id,
                        },
                      }"
                      variant="text"
                    >
                      Uredi uzvanika
                    </v-btn>
                    <v-btn
                      v-if="canEditInvitees"
                      class="button button--menu button--text-danger table-menu__button--danger"
                      :disabled="isEventArchived"
                      variant="text"
                      @click="openDeleteInviteeDialog(invitee)"
                    >
                      <v-icon
                        class="table-menu__icon"
                        icon="mdi-delete-outline"
                        size="20"
                      />
                      Ukloni uzvanika
                    </v-btn>
                  </div>
                </v-menu>
              </td>
            </tr>

            <tr v-if="!inviteeListState.eventInvitees.length">
              <td
                class="table__empty"
                colspan="4"
              >
                Nema uzvanika koji odgovaraju odabranim filterima.
              </td>
            </tr>
          </tbody>
        </v-table>

        <Pagination
          :page="inviteeListState.currentInviteeListPage"
          :per-page="inviteesPerPage"
          :total-results="inviteeListState.inviteeListTotalResults"
          @page-change="setInviteeListPage"
        />
      </article>
    </section>

    <AppConfirmDialog
      v-model="inviteeListState.deleteInviteeDialogOpen"
      title="Ukloni uzvanika?"
      :subtitle="inviteeListState.inviteeToDelete?.name"
      message="Jeste li sigurni da želite ukloniti ovog uzvanika s događaja?"
      primary-text="Ukloni"
      secondary-text="Odustani"
      @close="inviteeListState.inviteeToDelete = null"
      @confirm="deleteInviteeAsync"
    />
  </section>
</template>

<script setup>
import { computed, onWatcherCleanup, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AppConfirmDialog from '../../components/AppConfirmDialog.vue'
import AppPageTitleBar from '../../components/AppPageTitleBar.vue'
import Filters from '../../components/Filters.vue'
import Pagination from '../../components/Pagination.vue'
import ToolbarActionButton from '../../components/ToolbarActionButton.vue'
import { hasPermission, permissionNames } from '../../auth/authorization'
import { getPageCount } from '../../helpers'
import { apiClient } from '../../services/apiClient'
import { getActivityEventAsync } from '../../services/offlineService'
import { appStore, showRequestErrorToast, showSuccessToast } from '../../store/appStore'

const route = useRoute()
const router = useRouter()
const inviteesPerPage = 15

const inviteeListState = reactive({
  filters: {
    signedDocumentId: null,
    completedActivityId: null,
    search: null,
  },
  currentInviteeListPage: 1,
  inviteeListTotalResults: 0,
  eventInvitees: [],
  eventInviteeRecords: [],
  inviteeToDelete: null,
  deleteInviteeDialogOpen: false,
  isDownloadingQrCodes: false,
  importFeedback: null,
})

const eventId = computed(() => route.params.eventId)
const inviteeImportInput = ref(null)
const selectedEvent = ref(null)
const isEventArchived = computed(() => selectedEvent.value?.isArchived ?? false)
const canEditInvitees = computed(() => hasPermission(permissionNames.eventsInviteesEdit))
const canImportInvitees = computed(() => hasPermission(permissionNames.eventsInviteesImport))
const canDownloadInviteeQrCodes = computed(() => (
  hasPermission(permissionNames.eventsInviteesQrDownload)
))
const pageTitle = computed(() => selectedEvent.value
  ? `Uzvanici "${selectedEvent.value.name}"`
  : 'Uzvanici')
const backRoute = computed(() => ({
  name: 'event-detail',
  params: { eventId: eventId.value },
}))
const createInviteeRoute = computed(() => ({
  name: 'event-invitee-create',
  params: { eventId: eventId.value },
}))
const inviteeFilterFields = computed(() => [
  {
    key: 'signedDocumentId',
    type: 'select',
    placeholder: 'POTPISANI DOKUMENTI',
    items: (selectedEvent.value?.documents ?? []).map((document) => ({
      title: document.name,
      value: document.id,
    })),
    width: '300px',
  },
  {
    key: 'completedActivityId',
    type: 'select',
    placeholder: 'IZVRŠENE AKTIVNOSTI',
    items: (selectedEvent.value?.activities ?? []).map((activity) => ({
      title: activity.name,
      value: activity.id,
    })),
    width: '300px',
  },
  {
    key: 'search',
    type: 'search',
    placeholder: 'Pretraži po imenu, tvrtki ili e-mailu...',
    width: '100%',
  },
])

watch(
  [eventId, () => appStore.importingInviteesEventId === eventId.value],
  ([currentEventId, isImportingCurrentEvent], [previousEventId]) => {
    const eventChanged = currentEventId !== previousEventId
    if (eventChanged) {
      inviteeListState.filters = {
        signedDocumentId: null,
        completedActivityId: null,
        search: null,
      }
      inviteeListState.currentInviteeListPage = 1
      inviteeListState.importFeedback = null
    }
    if (isImportingCurrentEvent && !eventChanged) {
      return
    }
    if (!eventChanged && selectedEvent.value && inviteeListState.importFeedback?.type !== 'success') {
      return
    }
    getInviteesAsync(eventChanged)
  },
  { immediate: true }
)

watch(
  [
    () => inviteeListState.currentInviteeListPage,
    () => inviteeListState.filters.search,
    () => inviteeListState.filters.signedDocumentId,
    () => inviteeListState.filters.completedActivityId,
  ],
  setFilteredInvitees
)

function resetInviteeListPage () {
  inviteeListState.currentInviteeListPage = 1
}

function setInviteeListPage (selectedPage) {
  inviteeListState.currentInviteeListPage = selectedPage
}

async function getInviteesAsync (eventChanged) {
  let isCurrentRequest = true
  onWatcherCleanup(() => { isCurrentRequest = false })

  if (eventChanged) {
    selectedEvent.value = null
    inviteeListState.eventInviteeRecords = []
    inviteeListState.inviteeToDelete = null
    inviteeListState.deleteInviteeDialogOpen = false
    setFilteredInvitees()
  }

  if (appStore.importingInviteesEventId === eventId.value) {
    return
  }

  try {
    const event = await getActivityEventAsync(eventId.value)

    if (!isCurrentRequest) {
      return
    }

    setEventInvitees(event)
  } catch (error) {
    console.error('Failed to load event invitees.', error)
    if (isCurrentRequest) {
      showRequestErrorToast(error, eventChanged
        ? 'Uzvanike nije bilo moguće učitati.'
        : 'Popis uzvanika nije bilo moguće osvježiti.')
    }
  }
}

function setEventInvitees (event) {
  selectedEvent.value = event
  inviteeListState.eventInviteeRecords = event.invitees.map((invitee) => ({
    ...invitee,
    name: invitee.fullName,
    signedDocumentIds: invitee.documents
      .filter(({ signedAt, signedPdf }) => signedAt || signedPdf)
      .map(({ id }) => id),
    completedActivityIds: invitee.activities
      .filter(({ activityTypeId, finishedAt }) => finishedAt || event.activities
        .some(({ id, type }) => id === activityTypeId && type === 'classic'))
      .map(({ activityTypeId }) => activityTypeId),
  }))
  setFilteredInvitees()
}

function setFilteredInvitees () {
  const { search, signedDocumentId, completedActivityId } = inviteeListState.filters
  const searchTerm = search?.trim().toLowerCase()
  const filteredInvitees = inviteeListState.eventInviteeRecords.filter((invitee) => (
    (!searchTerm || [invitee.name, invitee.company, invitee.email]
      .some((value) => value.toLowerCase().includes(searchTerm))) &&
    (!signedDocumentId || invitee.signedDocumentIds.includes(signedDocumentId)) &&
    (!completedActivityId || invitee.completedActivityIds.includes(completedActivityId))
  ))
  const startIndex = (inviteeListState.currentInviteeListPage - 1) * inviteesPerPage

  inviteeListState.eventInvitees = filteredInvitees.slice(startIndex, startIndex + inviteesPerPage)
  inviteeListState.inviteeListTotalResults = filteredInvitees.length
}

function openDeleteInviteeDialog (invitee) {
  inviteeListState.inviteeToDelete = invitee
  inviteeListState.deleteInviteeDialogOpen = true
}

function openInviteeDetail (invitee) {
  router.push({
    name: 'event-invitee-detail',
    params: {
      eventId: eventId.value,
      inviteeId: invitee.id,
    },
  })
}

function openInviteeImport () {
  inviteeImportInput.value.click()
}

async function importInviteesAsync (event) {
  const file = event.target.files[0]
  event.target.value = ''

  if (!file || appStore.importingInviteesEventId !== null) {
    return
  }

  if (!file.name.toLowerCase().endsWith('.xlsx') || file.size > 1024 * 1024) {
    inviteeListState.importFeedback = {
      type: 'error',
      message: 'Odaberite Excel datoteku (.xlsx) veličine do 1 MB.',
      rows: [],
    }
    return
  }

  const importedEventId = eventId.value
  appStore.importingInviteesEventId = importedEventId
  inviteeListState.importFeedback = null

  try {
    const formData = new FormData()
    formData.append('file', file)
    const result = await apiClient.postAsync(
      `/api/events/${importedEventId}/invitees/import`,
      formData,
      { timeoutMs: 60000 }
    )

    if (eventId.value === importedEventId) {
      inviteeListState.importFeedback = {
        type: 'success',
        message: `Uvezeno: ${result.created.length}. Već pozvano: ${result.alreadyInvited.length}.`,
        rows: result.alreadyInvited,
      }
    }
  } catch (error) {
    console.error('Failed to import event invitees.', error)
    if (error.status === 400 && eventId.value === importedEventId) {
      inviteeListState.importFeedback = {
        type: 'error',
        message: error.data?.message ?? 'Datoteku nije bilo moguće uvesti.',
        rows: error.data?.rows ?? [],
      }
    } else if (eventId.value === importedEventId) {
      showRequestErrorToast(error, 'Uzvanike nije bilo moguće uvesti.')
    }
  } finally {
    appStore.importingInviteesEventId = null
  }
}

async function downloadInviteeQrCodesAsync () {
  inviteeListState.isDownloadingQrCodes = true

  try {
    const qrCodesArchive = await apiClient.getAsync(
      `/api/events/${eventId.value}/invitees`,
      { responseType: 'blob' }
    )
    const downloadUrl = URL.createObjectURL(qrCodesArchive)
    const downloadLink = window.document.createElement('a')

    downloadLink.href = downloadUrl
    downloadLink.download = `invitees-${eventId.value}.zip`
    downloadLink.click()
    URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    console.error('Failed to download event invitee QR codes.', error)
    showRequestErrorToast(error, 'QR kodove nije bilo moguće preuzeti.')
  } finally {
    inviteeListState.isDownloadingQrCodes = false
  }
}

async function deleteInviteeAsync () {
  const inviteeId = inviteeListState.inviteeToDelete.id

  try {
    await apiClient.deleteAsync(
      `/api/events/${eventId.value}/invitees/${inviteeId}`
    )

    inviteeListState.eventInviteeRecords = inviteeListState.eventInviteeRecords
      .filter(({ id }) => id !== inviteeId)

    const lastPage = getPageCount(inviteeListState.inviteeListTotalResults - 1, inviteesPerPage)
    const nextPage = Math.min(inviteeListState.currentInviteeListPage, lastPage)

    if (nextPage === inviteeListState.currentInviteeListPage) {
      setFilteredInvitees()
    } else {
      inviteeListState.currentInviteeListPage = nextPage
    }

    inviteeListState.inviteeToDelete = null
    showSuccessToast('Uzvanik je uspješno uklonjen.')
  } catch (error) {
    console.error('Failed to delete event invitee.', error)
    showRequestErrorToast(error, 'Uzvanika nije bilo moguće ukloniti.')
  }
}
</script>

<style
  scoped
  lang="scss"
>
.invitees-page {
  width: 100%;
  overflow-x: hidden;

  &__mobile-actions {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin: -12px 0 24px;
  }
}

.invitees-panel {
  @include breakpoint-up("sm") {
    min-height: 830px;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__import-rows {
    max-height: 240px;
    overflow-y: auto;
  }
}

.invitees-table {
  --table-min-width: 100%;
  --table-actions-width: 52px;

  &__row {
    cursor: pointer;
  }

  &__name {
    @include breakpoint-up("sm") {
      width: 26%;
    }
  }
}
</style>
